// common
import React, { useEffect, useRef } from 'react';
import { IoCloseOutline } from 'react-icons/io5';
import { useLocation, useNavigate } from 'react-router-dom';

import { ReactComponent as GoogleSVG } from 'assets/images/signup/signup_google.svg';
import { ReactComponent as KakaoSVG } from 'assets/images/signup/signup_kakao.svg';
import { ReactComponent as NaverSVG } from 'assets/images/signup/signup_naver.svg';
import { useRecoilState } from 'recoil';
import { userSNSState } from 'recoils/user/userState';
import styled from 'styled-components';

import InfoButtonGroup from 'components/button/InfoButtonGroup';

import useModalScroll from 'hooks/components/useModalScroll';
import useSNSLogin from 'hooks/user/useSNSLogin';

import { bilingInfoListConst } from 'constants/biling/bilingInfoListConst';

import { text } from 'styles/commons/text';

import BaseModal from '../../components/modal/BaseModal';

const SNSLoginModal = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [snsUserInfo, setUserSnsInfo] = useRecoilState(userSNSState);
  const { onKakaoLogin, onNaverLogin, onGoogleLogin } = useSNSLogin();
  const { preventScroll, allowScroll } = useModalScroll();
  const scrollYRef = useRef(0);

  // URL 파라미터 감지 (?open=login)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('open') === 'login') {
      setUserSnsInfo((prev) => ({ ...prev, isopen: true }));
    }
  }, [location.search, setUserSnsInfo]);

  useEffect(() => {
    if (snsUserInfo.isopen) {
      scrollYRef.current = preventScroll();
    }
    return () => {
      allowScroll(scrollYRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snsUserInfo.isopen]);

  const config = {
    title: 'SNS 로그인',
    icon: (
      <IoCloseOutline
        style={{ width: '24px', height: '24px', stroke: '#585858' }}
      />
    ),
    onClose: () => {
      const searchParams = new URLSearchParams(location.search);
      if (searchParams.get('open') === 'login') {
        searchParams.delete('open');
        searchParams.delete('redirect');
        const restQuery = searchParams.toString();
        const newPath = location.pathname + (restQuery ? `?${restQuery}` : '');
        navigate(newPath, { replace: true });
      } else {
        const findBackTo = bilingInfoListConst.find((el) =>
          location.pathname.includes(el.path)
        );
        if (findBackTo) {
          navigate(-1);
        }
      }
      setUserSnsInfo((prev) => ({ ...prev, isopen: false }));
    },
    loginButtons: [
      {
        text: '카카오로 로그인',
        className: 'signup_kakao_btn',
        beforeIcon: <KakaoSVG />,
        onClick: onKakaoLogin,
        visible: snsUserInfo.sns === 'kakao',
        info: '최근 로그인한 계정',
        isClose: false,
        isOutside: false,
        arrow: 'top',
      },
      {
        text: '네이버로 로그인',
        className: 'signup_naver_btn',
        beforeIcon: <NaverSVG />,
        onClick: onNaverLogin,
        visible: snsUserInfo.sns === 'naver',
        info: '최근 로그인한 계정',
        isClose: false,
        isOutside: false,
        arrow: 'top',
      },
      {
        text: '구글로 로그인',
        className: 'signup_google_btn',
        beforeIcon: <GoogleSVG />,
        onClick: onGoogleLogin,
        visible: snsUserInfo.sns === 'google',
        info: '최근 로그인한 계정',
        isClose: false,
        isOutside: false,
        arrow: 'top',
      },
    ],
  };

  return (
    <BaseModal
      title={config.title}
      visible={snsUserInfo.isopen}
      className="signup_modal"
      onClose={config.onClose}
      icon={config.icon}
    >
      <LoginInfoGroupBtn button={config.loginButtons} />
      <SignUpText
        color="gray900"
        onClick={() => {
          setUserSnsInfo((prev) => ({ ...prev, isopen: false }));
          navigate('/sign-up');
        }}
      >
        회원가입
      </SignUpText>
    </BaseModal>
  );
};

const LoginInfoGroupBtn = styled(InfoButtonGroup)`
  padding-bottom: 16px;
  gap: 8px;

  button {
    position: relative;
    width: 100%; // 버튼 가로 확장
    height: 44px;
    font-size: 14px;
    border-radius: 4px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      position: absolute;
      left: 30px;
      width: 45px;
      height: 45px;
    }

    // 카카오 버튼 스타일
    &.signup_kakao_btn {
      background: ${({ theme }) => theme.color.brand.kakao};
    }

    // 네이버 버튼 스타일
    &.signup_naver_btn {
      color: ${({ theme }) => theme.color.white};
      background: ${({ theme }) => theme.color.brand.naver};
    }

    // 구글 버튼 스타일
    &.signup_google_btn {
      background: ${({ theme }) => theme.color.brand.google};
    }
  }

  .info_button_box {
    left: 50% !important;
    transform: translateX(-50%) !important;
    bottom: 115% !important;

    &::before,
    &::after {
      left: 50% !important;
      transform: translateX(-50%) !important;
    }

    .info_content {
      padding: 10px 12px !important;
      text-align: center !important;
      min-width: fit-content !important;
    }
  }
`;

const SignUpText = styled(text.Link)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

SNSLoginModal.LoginInfoGroupBtn = LoginInfoGroupBtn;

export default SNSLoginModal;
